import { IndexV2Enum } from '@/types/translations/IndexV2Enum';
import {
  createBrandUrl,
  createBrandsUrl,
  createCategoryUrl,
  createCountyOfOriginUrl,
  createRegionUrl,
} from '@/utilites/search/create-search-urls';
import { IndexNavItem } from '@/types/constants/types';
import { IndexNavItemChildrenTypeEnum } from '@/types/constants/enums';
import { MegaMenuSection } from '@/types/mega-menu';
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';
import useEvents from './useEvents';
import { useAnalytics } from './useAnalytics';

export default function useNavItems() {
  const router = useRouter();
  const { getPageType } = useAnalytics();
  const {
    fireGlobalBrandButtonClickEvent,
    fireGlobalCountryButtonClickEvent,
    fireWhiskyCategoryButtonClickEvent,
    fireGinCategoryButtonClickEvent,
    fireRumCategoryButtonClickEvent,
    fireOtherCategoryButtonClickEvent,
  } = useEvents('', []);

  const goToCategoryPage = useCallback(
    (category: string) => {
      switch (category) {
        case 'whisky':
          fireWhiskyCategoryButtonClickEvent({ page_type: getPageType() });
          break;
        case 'gin':
          fireGinCategoryButtonClickEvent({ page_type: getPageType() });
          break;
        case 'rum':
          fireRumCategoryButtonClickEvent({ page_type: getPageType() });
          break;
        default:
          fireOtherCategoryButtonClickEvent({ page_type: getPageType() });
      }
      router.push(createCategoryUrl(category));
    },
    [
      router,
      fireWhiskyCategoryButtonClickEvent,
      fireGinCategoryButtonClickEvent,
      fireRumCategoryButtonClickEvent,
      fireOtherCategoryButtonClickEvent,
      getPageType,
    ]
  );

  const goToCountryPage = useCallback(
    (country: string) => {
      fireGlobalCountryButtonClickEvent({ page_type: getPageType(), country_name_page: country });
      router.push(createCountyOfOriginUrl(country));
    },
    [router, fireGlobalCountryButtonClickEvent, getPageType]
  );

  const goToBrandPage = useCallback(
    (brand: string) => {
      fireGlobalBrandButtonClickEvent({ page_type: getPageType(), brand_name_page: brand });
      router.push(createBrandUrl(brand));
    },
    [router, fireGlobalBrandButtonClickEvent, getPageType]
  );

const createNavItem = useCallback(
  (
    label: IndexV2Enum | string,
    href: string,
    onClick?: () => void,
    type: IndexNavItemChildrenTypeEnum = IndexNavItemChildrenTypeEnum.translate
  ) => {
    if (type === IndexNavItemChildrenTypeEnum.translate) {
      return {
        label: label as IndexV2Enum,
        href: href,
        ...(onClick && { onClick }),
        type: IndexNavItemChildrenTypeEnum.translate,
      } as {
        type: IndexNavItemChildrenTypeEnum.translate;
        label: IndexV2Enum;
        href: string;
        onClick?: () => void;
      };
    } else {
      return {
        label: label as string,
        href: href,
        ...(onClick && { onClick }),
        type: IndexNavItemChildrenTypeEnum.notranslate,
      } as {
        type: IndexNavItemChildrenTypeEnum.notranslate;
        label: string;
        href: string;
        onClick?: () => void;
      };
    }
  },
  []
);

  const indexNavItemsCountries: IndexNavItem[] = [
    {
      label: IndexV2Enum['home_nav.countries'],
      children: [
        createNavItem(
          IndexV2Enum['home_nav.country_scotland'],
          createCountyOfOriginUrl('Scotland'),
          () => goToCountryPage('Scotland'),
          IndexNavItemChildrenTypeEnum.translate
        ),
        createNavItem(
          IndexV2Enum['home_nav.country_taiwan'],
          createCountyOfOriginUrl('Taiwan'),
          () => goToCountryPage('Taiwan'),
          IndexNavItemChildrenTypeEnum.translate
        ),
        createNavItem(
          IndexV2Enum['home_nav.country_usa'],
          createCountyOfOriginUrl('USA'),
          () => goToCountryPage('USA'),
          IndexNavItemChildrenTypeEnum.translate
        ),
        createNavItem(
          IndexV2Enum['home_nav.country_japan'],
          createCountyOfOriginUrl('Japan'),
          () => goToCountryPage('Japan'),
          IndexNavItemChildrenTypeEnum.translate
        ),
        createNavItem(
          IndexV2Enum['home_nav.country_ireland'],
          createCountyOfOriginUrl('Ireland'),
          () => goToCountryPage('Ireland'),
          IndexNavItemChildrenTypeEnum.translate
        ),
        createNavItem(
          IndexV2Enum['home_nav.country_england'],
          createCountyOfOriginUrl('England'),
          () => goToCountryPage('England'),
          IndexNavItemChildrenTypeEnum.translate
        ),
        createNavItem(
          IndexV2Enum['home_nav.country_australia'],
          createCountyOfOriginUrl('Australia'),
          () => goToCountryPage('Australia'),
          IndexNavItemChildrenTypeEnum.translate
        ),
        createNavItem(
          IndexV2Enum['home_nav.country_germany'],
          createCountyOfOriginUrl('Germany'),
          () => goToCountryPage('Germany'),
          IndexNavItemChildrenTypeEnum.translate
        ),
        createNavItem(
          IndexV2Enum['home_nav.country_worldwide'],
          createCountyOfOriginUrl('Worldwide'),
          () => goToCountryPage('Worldwide'),
          IndexNavItemChildrenTypeEnum.translate
        ),
      ],
    },
  ];

  const indexMegaMenuCountries = useMemo<MegaMenuSection[]>(() => {
    const createCountryMenuItem = ({
      id,
      label,
      country,
    }: {
      id: string;
      label: string;
      country: string;
    }) => ({
      id,
      label,
      href: createCountyOfOriginUrl(country),
      onClick: () => goToCountryPage(country),
    });

    const createRegionMenuItem = (region: string) => ({
      id: `countries-region-${region.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
      label: region,
      href: createRegionUrl(region),
    });

    return [
      {
        id: 'countries-scotland',
        title: 'Scotland',
        items: [
          'Speyside',
          'Islay',
          'Highland',
          'Lowland',
          'Campbeltown',
          'Island',
        ].map(createRegionMenuItem),
      },
      {
        id: 'countries-europe',
        title: 'Europe',
        items: [
          createCountryMenuItem({ id: 'countries-europe-ireland', label: 'Ireland', country: 'Ireland' }),
          createCountryMenuItem({ id: 'countries-europe-england', label: 'England', country: 'England' }),
          createCountryMenuItem({ id: 'countries-europe-germany', label: 'Germany', country: 'Germany' }),
          createCountryMenuItem({ id: 'countries-europe-france', label: 'France', country: 'France' }),
          createCountryMenuItem({ id: 'countries-europe-spain', label: 'Spain', country: 'Spain' }),
          createCountryMenuItem({ id: 'countries-europe-italy', label: 'Italy', country: 'Italy' }),
          createCountryMenuItem({ id: 'countries-europe-nordics', label: 'Nordics', country: 'Nordics' }),
        ],
      },
      {
        id: 'countries-asia',
        title: 'Asia',
        items: [
          createCountryMenuItem({ id: 'countries-asia-japan', label: 'Japan', country: 'Japan' }),
          createCountryMenuItem({ id: 'countries-asia-india', label: 'India', country: 'India' }),
          createCountryMenuItem({ id: 'countries-asia-taiwan', label: 'Taiwan', country: 'Taiwan' }),
          createCountryMenuItem({ id: 'countries-asia-china', label: 'China', country: 'China' }),
          createCountryMenuItem({ id: 'countries-asia-korea', label: 'Korea', country: 'Korea' }),
        ],
      },
      {
        id: 'countries-america-caribbean',
        title: 'America & Caribbean',
        items: [
          createCountryMenuItem({
            id: 'countries-america-usa',
            label: 'United States',
            country: 'USA',
          }),
          createCountryMenuItem({ id: 'countries-america-canada', label: 'Canada', country: 'Canada' }),
          createCountryMenuItem({ id: 'countries-america-mexico', label: 'Mexico', country: 'Mexico' }),
          createCountryMenuItem({ id: 'countries-america-jamaica', label: 'Jamaica', country: 'Jamaica' }),
          createCountryMenuItem({ id: 'countries-america-guyana', label: 'Guyana', country: 'Guyana' }),
          createCountryMenuItem({ id: 'countries-america-barbados', label: 'Barbados', country: 'Barbados' }),
        ],
      },
    ];
  }, [goToCountryPage]);

  const indexNavItemsBrands: IndexNavItem[] = [
    {
      label: IndexV2Enum['home_nav.brand'],
      children: [
        createNavItem(
          'Macallan',
          createBrandUrl('Macallan'),
          () => goToBrandPage('Macallan'),
          IndexNavItemChildrenTypeEnum.notranslate
        ),
        createNavItem(
          'Ardbeg',
          createBrandUrl('Ardbeg'),
          () => goToBrandPage('Ardbeg'),
          IndexNavItemChildrenTypeEnum.notranslate
        ),
        createNavItem(
          'Yamazaki',
          createBrandUrl('Yamazaki'),
          () => goToBrandPage('Yamazaki'),
          IndexNavItemChildrenTypeEnum.notranslate
        ),
        createNavItem(
          'Chichibu',
          createBrandUrl('Chichibu'),
          () => goToBrandPage('Chichibu'),
          IndexNavItemChildrenTypeEnum.notranslate
        ),
        createNavItem(
          'Bowmore',
          createBrandUrl('Bowmore'),
          () => goToBrandPage('Bowmore'),
          IndexNavItemChildrenTypeEnum.notranslate
        ),
        createNavItem(
          'Glenfiddich',
          createBrandUrl('Glenfiddich'),
          () => goToBrandPage('Glenfiddich'),
          IndexNavItemChildrenTypeEnum.notranslate
        ),
        createNavItem(
          'Glenlivet',
          createBrandUrl('Glenlivet'),
          () => goToBrandPage('Glenlivet'),
          IndexNavItemChildrenTypeEnum.notranslate
        ),
        createNavItem(
          'Glenmorangie',
          createBrandUrl('Glenmorangie'),
          () => goToBrandPage('Glenmorangie'),
          IndexNavItemChildrenTypeEnum.notranslate
        ),
      ],
    },
  ];

  const indexMegaMenuBrands = useMemo<MegaMenuSection[]>(() => {
    const createBrandMenuItem = (brand: string) => ({
      id: `brands-menu-${brand.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
      label: brand,
      href: createBrandUrl(brand),
      onClick: () => goToBrandPage(brand),
    });

    return [
      {
        id: 'top-brands',
        title: 'Top Brands',
        items: [
          'Ardbeg',
          'Bowmore',
          'Glenfiddich',
          'Glenmorangie',
          'Hibiki',
          'Johnnie Walker',
          'Laphroaig',
          'Macallan',
          'Midleton',
          'Yamazaki',
        ].map(createBrandMenuItem),
        footerLink: {
          id: 'brands-menu-all-brands',
          label: 'Show all Brands',
          href: createBrandsUrl(),
        },
      },
      {
        id: 'trending-brands',
        title: 'Trending Brands',
        items: [
          'Ardnahoe',
          'Benriach',
          'Chichibu',
          'Chivas Regal',
          'Dalmore',
          'Glendronach',
          'Highland Park',
          'Redbreast',
          'Royal Salute',
          'Springbank',
        ].map(createBrandMenuItem),
      },
      {
        id: 'bourbon-american',
        title: 'Bourbon & American',
        items: [
          "Blanton's",
          "Booker's",
          'Eagle Rare',
          "Jack Daniel's",
          'Jim Beam',
          "Maker's Mark",
          "Michter's",
          'Pappy Van Winkle',
          'Weller',
          'Woodford Reserve',
        ].map(createBrandMenuItem),
      },
      {
        id: 'spirits-rum',
        title: 'Spirits & Rum',
        items: [
          'Clase Azul',
          'Diplomatico',
          'Don Julio',
          'Gin Mare',
          'Mangabeiras',
          'Hennessy',
          'Martell',
          'Monkey 47',
          'Remy Martin',
          'Ron Zacapa',
        ].map(createBrandMenuItem),
      },
    ];
  }, [goToBrandPage]);

  return {
    indexNavItemsCountries,
    indexMegaMenuCountries,
    indexNavItemsBrands,
    indexMegaMenuBrands,
  };
}